/**
 * Tiny browser-safe JWT helpers.
 *
 * We only need to read the `exp` claim from Google ID tokens so we can detect
 * an expired session before sending a request — there is no signature
 * verification done client-side (the API gateway is responsible for that).
 */

export interface JwtClaims {
  exp?: number;
  iat?: number;
  email?: string;
  [key: string]: unknown;
}

function base64UrlDecode(input: string): string {
  const padded = input + '='.repeat((4 - (input.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeJwt(token: string): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json);
    return typeof claims === 'object' && claims !== null ? claims : null;
  } catch {
    return null;
  }
}

export function getTokenExp(token: string): number | null {
  const claims = decodeJwt(token);
  if (!claims) return null;
  return typeof claims.exp === 'number' ? claims.exp : null;
}

/**
 * Returns true if the token is missing an `exp` claim *or* `exp` is in the
 * past. A small clock-skew margin makes us consider the token expired slightly
 * before its real expiry so we never send a token that's about to bounce.
 */
export function isTokenExpired(token: string, clockSkewSeconds: number = 10): boolean {
  const exp = getTokenExp(token);
  if (exp == null) return false;
  return Date.now() / 1000 >= exp - clockSkewSeconds;
}
